/**
 * Centralised reactive store of resolved feature flags for the
 * current user (enterprise + station). Hydrated at login from
 * `/api/v1/features/me`. The map is `{ feature_key → { enabled,
 * value, source } }`.
 *
 * Cascade resolution happens server-side; the frontend is read-only.
 */
import { ref, computed } from "vue";

export type FeatureSource = "station_override" | "enterprise" | "default" | "unknown";

export interface FeatureResolved {
	enabled: boolean;
	value: any;
	source: FeatureSource;
}

export const featuresMap = ref<Record<string, FeatureResolved>>({});
export const featuresLoadedAt = ref<number>(0);

export function setFeatures(map: Record<string, FeatureResolved>) {
	featuresMap.value = map || {};
	featuresLoadedAt.value = Date.now();
}

export function clearFeatures() {
	featuresMap.value = {};
	featuresLoadedAt.value = 0;
}

export function isFeatureEnabledRaw(key: string): boolean {
	return !!featuresMap.value[key]?.enabled;
}

export function getFeatureValueRaw<T = any>(key: string, fallback: T | null = null): T | null {
	const v = featuresMap.value[key]?.value;
	return v == null ? fallback : (v as T);
}

export const featuresCount = computed(() => Object.keys(featuresMap.value).length);
